/**
 * The Clerk session token, held somewhere `api.ts` can reach it.
 *
 * A module-level holder rather than a hook, because `api.ts` is a plain fetch
 * wrapper called from effects, callbacks and event handlers — everywhere a hook
 * cannot be. It is a deliberate exception to the "no globals" rule, and it is
 * safe for one specific reason: the value is not a durable credential.
 *
 * Clerk's session token is short-lived (60s by default), and the value here is
 * never written to storage — it lives only in this tab's memory and is re-read
 * from Clerk on demand. An XSS bug cannot steal anything that outlives the tab.
 * What it *can* still do is make API calls as the signed-in user, which is
 * exactly what it could do with Clerk's own cookie anyway.
 *
 * This file exists as its own module so `api.ts` and `auth.tsx` can both import
 * it without importing each other. Putting the holder in `auth.tsx` would create
 * a cycle, since `auth.tsx` already imports `api.ts`.
 */

type TokenGetter = () => Promise<string | null>;

let getToken: TokenGetter | null = null;

/** Registered once by <SessionBridge />, which lives inside <ClerkProvider>. */
export function setTokenGetter(getter: TokenGetter): void {
  getToken = getter;
}

export function clearTokenGetter(): void {
  getToken = null;
}

/**
 * Clerk's `getToken` can remain pending forever when its CDN is blocked: the
 * script failed, but the promise the provider exposed never settles. Every
 * `api.get` awaited that promise before even issuing its fetch, which is why
 * the public wall, guest upload page and gallery all sat on “Loading…” even
 * though none of them needs a host session.
 *
 * A short deadline keeps public surfaces usable. A host request that races the
 * token simply goes out anonymously and gets a clean 401; the auth provider
 * retries after Clerk reports it is loaded.
 */
const TOKEN_TIMEOUT_MS = 1_500;

export async function readSessionToken(): Promise<string | null> {
  if (!getToken) return null;
  try {
    return await Promise.race([
      getToken(),
      new Promise<null>((resolve) => setTimeout(() => resolve(null), TOKEN_TIMEOUT_MS)),
    ]);
  } catch {
    // A token fetch can fail transiently while Clerk rehydrates. Sending no
    // header makes the request anonymous, which the server answers 401 — the
    // correct answer, and better than sending something half-formed.
    return null;
  }
}